<template>
  <nav class="sb-nav">
    <template v-for="(section, si) in menu">
      <div v-if="section.key || section.text" :key="'sec' + si" class="sb-section">
        {{ section.key ? $t(section.key) : section.text }}
      </div>

      <template v-for="(item, ii) in section.items">
        <!-- Leaf item (no children) -->
        <button
          v-if="!item.items"
          :key="'l' + si + '-' + ii"
          class="sb-item"
          :class="{ active: isActive(item) }"
          @click="navigate(item.link)"
        >
          <i :class="iconClass(item.icon)" />
          <span class="label">{{ item.key ? $t(item.key) : item.text }}</span>
          <span v-if="itemBadge(item) > 0" class="badge">
            {{ itemBadge(item) }}
          </span>
        </button>

        <!-- Group header -->
        <button
          v-if="item.items"
          :key="'g' + si + '-' + ii"
          class="sb-item has-children"
          :class="{ open: openGroups[item.text], active: isGroupActive(item) && !openGroups[item.text] }"
          @click="toggleGroup(item.text)"
        >
          <i :class="iconClass(item.icon)" />
          <span class="label">{{ item.key ? $t(item.key) : item.text }}</span>
          <span v-if="groupBadgeTotal(item) > 0" class="badge">{{ groupBadgeTotal(item) }}</span>
          <i class="mdi mdi-chevron-right chevron" />
        </button>

        <!-- Group sub-list (level 2) -->
        <div
          v-if="item.items"
          :key="'s' + si + '-' + ii"
          class="sb-sub"
          :style="{ maxHeight: openGroups[item.text] ? subMaxHeight(item) + 'px' : '0' }"
        >
          <template v-for="(child, ci) in item.items">
            <!-- Level-2 leaf -->
            <button
              v-if="!child.items"
              :key="'cl' + ci"
              class="sb-item"
              :class="{ active: isActive(child) }"
              @click="navigate(child.link)"
            >
              <i :class="iconClass(child.icon)" />
              <span class="label">{{ child.key ? $t(child.key) : child.text }}</span>
              <span v-if="itemBadge(child) > 0" class="badge">
                {{ itemBadge(child) }}
              </span>
            </button>

            <!-- Level-2 group header -->
            <button
              v-if="child.items"
              :key="'cg' + ci"
              class="sb-item has-children"
              :class="{ open: openGroups[child.text], active: isGroupActive(child) && !openGroups[child.text] }"
              @click="toggleGroup(child.text)"
            >
              <i :class="iconClass(child.icon)" />
              <span class="label">{{ child.key ? $t(child.key) : child.text }}</span>
              <i class="mdi mdi-chevron-right chevron" />
            </button>

            <!-- Level-2 sub-list (level 3) -->
            <div
              v-if="child.items"
              :key="'cs' + ci"
              class="sb-sub"
              :style="{ maxHeight: openGroups[child.text] ? subMaxHeight(child) + 'px' : '0' }"
            >
              <button
                v-for="(gc, gi) in child.items"
                :key="'gc' + gi"
                class="sb-item"
                :class="{ active: isActive(gc) }"
                @click="navigate(gc.link)"
              >
                <i :class="iconClass(gc.icon)" />
                <span class="label">{{ gc.key ? $t(gc.key) : gc.text }}</span>
                <span v-if="itemBadge(gc) > 0" class="badge">
                  {{ itemBadge(gc) }}
                </span>
              </button>
            </div>
          </template>
        </div>
      </template>
    </template>
  </nav>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
  props: {
    menu: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      openGroups: {}
    };
  },

  computed: {
    ...mapGetters('counters', ['getCounter'])
  },

  watch: {
    $route: {
      immediate: true,
      handler() {
        this.autoOpenActiveGroups();
      }
    }
  },

  methods: {
    iconClass(icon) {
      if (!icon) return 'mdi mdi-circle-medium';
      if (icon.startsWith('mdi ')) return icon;
      return 'mdi ' + icon;
    },

    isActive(item) {
      const path = this.$route?.path || '';
      if (!item.link) return false;
      if (item.link2 && path === item.link2) return true;
      if (item.regex) return item.regex.test(path);
      return path === item.link || (item.link !== '/' && path.startsWith(item.link + '/'));
    },

    isGroupActive(item) {
      if (!item.items) return this.isActive(item);
      const path = this.$route?.path || '';
      if (item.regex && item.regex.test(path)) return true;
      return item.items.some(child => child.items ? this.isGroupActive(child) : this.isActive(child));
    },

    toggleGroup(label) {
      this.$set(this.openGroups, label, !this.openGroups[label]);
    },

    navigate(link) {
      if (!link) return;
      if (this.$route.path !== link) {
        this.$router.push(link).catch(() => {});
      }
    },

    subMaxHeight(item) {
      if (!item.items) return 0;
      let h = 0;
      for (const child of item.items) {
        h += 31;
        if (child.items && this.openGroups[child.text]) {
          h += this.subMaxHeight(child);
        }
      }
      return h;
    },

    /**
     * Total del badge de un ítem. Acepta `counterKey` (un contador) o
     * `counterKeys` (varios, sumados) para los módulos donde más de una cosa
     * requiere atención — ej: Visitas Datacenter suma las solicitudes por
     * aprobar y las personas actualmente dentro.
     */
    itemBadge(item) {
      if (!item) return 0;
      const keys = item.counterKeys || (item.counterKey ? [item.counterKey] : []);
      return keys.reduce((total, key) => total + (this.getCounter(key) || 0), 0);
    },

    groupBadgeTotal(item) {
      if (!item.items) return 0;
      let total = 0;
      const sum = (items) => {
        items.forEach(i => {
          total += this.itemBadge(i);
          if (i.items) sum(i.items);
        });
      };
      sum(item.items);
      return total;
    },

    autoOpenActiveGroups() {
      for (const section of this.menu) {
        if (!section.items) continue;
        for (const item of section.items) {
          if (item.items && this.isGroupActive(item)) {
            this.$set(this.openGroups, item.text, true);
            for (const child of item.items) {
              if (child.items && this.isGroupActive(child)) {
                this.$set(this.openGroups, child.text, true);
              }
            }
          }
        }
      }
    }
  }
};
</script>

<style scoped>
.sb-nav {
  flex: 1;
  overflow-y: auto;
  padding: 4px 8px 16px;
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.sb-section {
  font-size: 10.5px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: var(--text-3, #6d7585);
  padding: 14px 10px 6px;
}

.sb-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 10px;
  height: 34px;
  border-radius: 8px;
  color: var(--text-2, #5b6370);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  border: 0;
  background: transparent;
  width: 100%;
  text-align: left;
  position: relative;
  transition: background 120ms, color 120ms;
  flex-shrink: 0;
}

.sb-item:hover {
  background: var(--hover, rgba(15, 23, 42, 0.04));
  color: var(--text, #1b1d22);
}

.sb-item.active {
  background: var(--acc-bg, rgba(0, 150, 199, 0.10));
  color: var(--acc, #0085b3);
  font-weight: 600;
}

.sb-item.active::before {
  content: "";
  position: absolute;
  left: -8px;
  top: 8px;
  bottom: 8px;
  width: 3px;
  border-radius: 2px;
  background: var(--acc, #0096c7);
}

.sb-item .mdi {
  font-size: 17px;
  flex-shrink: 0;
  opacity: 0.85;
}

.sb-item.active .mdi {
  opacity: 1;
  color: var(--acc, #0096c7);
}

.sb-item .label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sb-item .badge {
  background: var(--danger, #d04545);
  color: white;
  font-size: 10.5px;
  font-weight: 600;
  height: 18px;
  min-width: 18px;
  padding: 0 5px;
  border-radius: 9px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.sb-item.has-children .chevron {
  font-size: 16px;
  opacity: 0.6;
  flex-shrink: 0;
  transition: transform 200ms;
}

.sb-item.open .chevron {
  transform: rotate(90deg);
}

.sb-sub {
  overflow: hidden;
  transition: max-height 240ms cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  gap: 1px;
  padding-left: 22px;
}

.sb-sub .sb-item {
  height: 30px;
  font-size: 12.5px;
}

.sb-sub .sb-item .mdi {
  font-size: 14.5px;
}

/* Dark theme adjustments */
.theme--dark .sb-section,
.theme-dark .sb-section {
  color: #6d7585;
}

.theme--light .sb-section,
.theme-light .sb-section {
  color: #8a90a0;
}

.theme--dark .sb-item,
.theme-dark .sb-item {
  color: #a4abb7;
}

.theme--dark .sb-item:hover,
.theme-dark .sb-item:hover {
  background: rgba(255, 255, 255, 0.04);
  color: #e8eaef;
}

.theme--dark .sb-item.active,
.theme-dark .sb-item.active {
  filter: brightness(1.35);
}
</style>
